import { useEffect, useState } from "react";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { formSurfaceSx } from "constants/pageSurfaces";
import Breadcrumbs from "components/common/Breadcrumbs";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "services/api.service";
import useEmployee from "hooks/employees/useEmployee";
import useLocations from "hooks/locations/useLocations";
import EmployeeForm, { employeeToForm, EmployeeFormValues } from "./EmployeeForm";

export default function EmployeeEdit() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { employee, isLoading, isError } = useEmployee(uuid);
  const { locations } = useLocations();
  const [values, setValues] = useState<EmployeeFormValues | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employee) {
      setValues(employeeToForm(employee));
    }
  }, [employee]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!uuid || !values) return;
    setError(null);
    const payload = {
      ...values,
      officeLocationId: values.officeLocationId.trim() || null,
      externalEmployeeId: values.externalEmployeeId.trim() || null,
      homeAddress: values.homeAddress.trim() || null,
      homeAddress2: values.homeAddress2.trim() || null,
      homeCity: values.homeCity.trim() || null,
      homeState: values.homeState.trim() || null,
      homeZipcode: values.homeZipcode.trim() || null,
      homeCountry: values.homeCountry.trim() || null,
    };
    api
      .put(`/employees/${uuid}`, { employee: payload })
      .then(() => navigate("/employees"))
      .catch((err) => {
        const msg =
          err?.response?.data?.errors?.join?.(", ") || "Could not update employee";
        setError(String(msg));
      });
  };

  if (isLoading && !employee) {
    return (
      <Box display='flex' justifyContent='center' mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !employee || !values) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        Employee not found.
      </Alert>
    );
  }

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            { label: 'Dashboard', to: '/dashboard' },
            { label: 'Employees', to: '/employees' },
            { label: 'Edit' },
          ]}
        />
      </Box>
      <Box sx={formSurfaceSx}>
        <Typography variant='h4' mb={2} fontWeight='bold'>
          Edit employee
        </Typography>
        {error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <EmployeeForm
          values={values}
          onChange={setValues}
          onSubmit={handleSubmit}
          submitLabel='Save'
          cancelTo='/employees'
          locations={locations}
          assignedAssets={employee.assets ?? []}
        />
      </Box>
    </div>
  );
}
